import { useCallback } from 'react';

/**
 * Custom hook to use local storage when need to pass data between components from different views to skip heavy API calls.
 * @param {string} key - key for local storage
 * @returns {setToLocalStorage, getFromLocalStorage, removeFromLocalStorage}
 * setToLocalStorage - function to set value to local storage
 * getFromLocalStorage - function to get value from local storage
 * removeFromLocalStorage - function to remove value from local storage
 * @example
 * const {setToLocalStorage, getFromLocalStorage, removeFromLocalStorage} = useLocalStorage();
 * setToLocalStorage('key', data);
 * const siteForEdit = getFromLocalStorage('key');
 * removeFromLocalStorage('key');
 */

export const useLocalStorage = () => {
  const setToLocalStorage = useCallback((key, value) => {
    try {
      const stringValue = (typeof value === 'object' && value) ? JSON.stringify(value) : value;
      return window.localStorage.setItem(key, stringValue);
    } catch (error) {
      // silently return;
      return null;
    }
  }, []);

  const getFromLocalStorage = useCallback((key) => {
    try {
      const item = window.localStorage.getItem(key);
      // check if the item is a stringified object
      return (item.startsWith('{') || item.startsWith('[')) && (item.endsWith('}') || item.endsWith(']')) ? JSON.parse(item) : item;
    } catch (error) {
      // silently return;
      return null;
    }
  }, []);

  const removeFromLocalStorage = useCallback((key) => {
    try {
      return window.localStorage.removeItem(key);
    } catch (error) {
      // silently return;
      return null;
    }
  }, []);

  return { setToLocalStorage, getFromLocalStorage, removeFromLocalStorage };
};
